"use client";

import { useState, type ReactNode } from "react";

import { useIntersectionObserver } from "@/shared/lib/hooks/use-intersection-observer";

/**
 * Монтирует детей, когда блок подходит к экрану, и больше не размонтирует.
 *
 * Тяжёлые секции ниже первого экрана (таблицы игроков, сетка участников, сетка
 * матчей) рендерились вместе с первым экраном и держали поток одной задачей
 * гидрации в секунду и больше (KAN-1243). Заглушка держит `minHeight`, чтобы
 * страница не прыгала, пока секция не смонтирована.
 *
 * `force` монтирует сразу — когда к секции ведёт действие пользователя
 * (выбран игрок, открыта ссылка на блок) и ждать прокрутки нельзя.
 */
export function DeferredMount({
  children,
  minHeight,
  force = false,
  // 200px: секция успевает смонтироваться до появления на экране при обычной
  // прокрутке, но не цепляет первый экран — при 600px таблица игроков под
  // пьедесталом монтировалась вместе с ним и ~1,2 тыс. узлов оставались в гидрации.
  rootMargin = "200px 0px",
}: {
  children: ReactNode;
  /** Примерная высота секции, px — место под неё до монтирования. */
  minHeight: number;
  force?: boolean;
  rootMargin?: string;
}) {
  const noObserver =
    typeof window !== "undefined" && !("IntersectionObserver" in window);
  const { ref, isIntersecting } = useIntersectionObserver({
    rootMargin,
    freezeOnceVisible: true,
  });

  // Защёлка: смонтированная секция не пропадает, когда `force` снова false.
  const [mounted, setMounted] = useState(false);
  const wanted = force || isIntersecting || noObserver;
  if (wanted && !mounted) setMounted(true);

  if (wanted || mounted) return <>{children}</>;
  return <div ref={ref} aria-hidden style={{ minHeight }} />;
}
